// Messages.js - Reusable messaging component
// Used in both customer and provider dashboards
// Includes Confirm/Decline buttons for providers
// Real-time updates using Firestore onSnapshot listener

import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp
} from 'firebase/firestore';

function Messages({ userRole }) {
  // userRole is either 'customer' or 'provider'
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [confirmingJob, setConfirmingJob] = useState(false);

  // Ref to scroll to bottom of messages automatically
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Fetch current user's profile
    const fetchUserData = async () => {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    };

    fetchUserData();

    // Fetch all conversations this user is part of
    const fetchConversations = async () => {
      try {
        const convsQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', user.uid)
        );

        const convsSnapshot = await getDocs(convsQuery);
        const convsList = convsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by last message time — most recent first
        convsList.sort((a, b) => {
          const timeA = a.lastMessageAt?.toDate ? a.lastMessageAt.toDate() : new Date(0);
          const timeB = b.lastMessageAt?.toDate ? b.lastMessageAt.toDate() : new Date(0);
          return timeB - timeA;
        });

        setConversations(convsList);

        // Auto open first conversation if exists
        if (convsList.length > 0 && !activeConversation) {
          setActiveConversation(convsList[0]);
        }

      } catch (err) {
        console.error('Error fetching conversations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for real time message updates when active conversation changes
  useEffect(() => {
    if (!activeConversation) return;

    // onSnapshot fires every time messages collection changes
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'conversations', activeConversation.id, 'messages'),
        orderBy('createdAt', 'asc')
      ),
      (snapshot) => {
        const currentUser = auth.currentUser;
        const messagesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMessages(messagesList);

        // Mark incoming messages as read by adding current user's uid to readBy array
        snapshot.docs.forEach(docSnap => {
          const msg = docSnap.data();
          if (currentUser && msg.senderId !== currentUser.uid && !(msg.readBy || []).includes(currentUser.uid)) {
            updateDoc(docSnap.ref, { readBy: arrayUnion(currentUser.uid) });
          }
        });

        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    );

    return () => unsubscribe();
  }, [activeConversation]);

  // Sends a new message to the active conversation
  const handleSendMessage = async () => {
    const user = auth.currentUser;
    if (!user || !newMessage.trim() || newMessage.length > 1000 || !activeConversation) return;

    try {
      const messageText = newMessage.trim();
      setNewMessage('');

      // Add message to messages subcollection
      await addDoc(
        collection(db, 'conversations', activeConversation.id, 'messages'),
        {
          text: messageText,
          senderId: user.uid,
          senderName: `${userData?.firstName} ${userData?.lastName}`,
          senderRole: userRole,
          type: 'message', // regular message type
          readBy: [user.uid], // sender has already read their own message
          createdAt: serverTimestamp(),
        }
      );

      // Update conversation last message preview
      await setDoc(doc(db, 'conversations', activeConversation.id), {
        ...activeConversation,
        lastMessage: messageText,
        lastMessageAt: serverTimestamp(),
        lastMessageSenderId: user.uid,
      }, { merge: true });

      // Notify the recipient that they have a new message
      const recipientId = userRole === 'customer'
        ? activeConversation.providerId
        : activeConversation.customerId;
      if (recipientId) {
        await addDoc(collection(db, 'notifications'), {
          userId: recipientId,
          message: `New message from ${userData?.firstName}`,
          type: 'message',
          read: false,
          createdAt: serverTimestamp(),
        });
      }

    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  // Called when provider clicks Confirm Job
  // Creates a booking and sends a confirmation message
  const handleConfirmJob = async () => {
    const user = auth.currentUser;
    if (!user || !activeConversation) return;

    try {
      setConfirmingJob(true);

      // Update conversation status to confirmed
      await updateDoc(doc(db, 'conversations', activeConversation.id), {
        status: 'confirmed',
        confirmedAt: serverTimestamp(),
        confirmedBy: user.uid,
      });

      // Send a system confirmation message in the chat
      await addDoc(
        collection(db, 'conversations', activeConversation.id, 'messages'),
        {
          text: `✅ ${userData?.firstName} has confirmed this visit. A booking has been created on your dashboard.`,
          senderId: user.uid,
          senderName: `${userData?.firstName} ${userData?.lastName}`,
          senderRole: 'provider',
          type: 'confirmation', // special type for styling
          createdAt: serverTimestamp(),
        }
      );

      // Update active conversation in local state
      setActiveConversation(prev => ({ ...prev, status: 'confirmed' }));
      setConversations(prev => prev.map(c =>
        c.id === activeConversation.id ? { ...c, status: 'confirmed' } : c
      ));

    } catch (err) {
      console.error('Error confirming job:', err);
    } finally {
      setConfirmingJob(false);
    }
  };

  // Called when provider clicks Decline
  // Sends a decline message and closes the conversation
  const handleDeclineJob = async () => {
    const user = auth.currentUser;
    if (!user || !activeConversation) return;

    try {
      // Update conversation status to declined
      await updateDoc(doc(db, 'conversations', activeConversation.id), {
        status: 'declined',
        declinedAt: serverTimestamp(),
        declinedBy: user.uid,
      });

      // Send a system decline message
      await addDoc(
        collection(db, 'conversations', activeConversation.id, 'messages'),
        {
          text: `❌ ${userData?.firstName} is unavailable for this visit. Please check your dashboard to find another caretaker in your area.`,
          senderId: user.uid,
          senderName: `${userData?.firstName} ${userData?.lastName}`,
          senderRole: 'provider',
          type: 'decline', // special type for styling
          createdAt: serverTimestamp(),
        }
      );

      // Update local state
      setActiveConversation(prev => ({ ...prev, status: 'declined' }));
      setConversations(prev => prev.map(c =>
        c.id === activeConversation.id ? { ...c, status: 'declined' } : c
      ));

    } catch (err) {
      console.error('Error declining job:', err);
    }
  };

  // Converts a Firestore timestamp to a human-friendly relative string
  const getRelativeTime = (timestamp) => {
    if (!timestamp?.toDate) return '';
    const now = new Date();
    const then = timestamp.toDate();
    const seconds = Math.floor((now - then) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Press Enter to send message
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const user = auth.currentUser;

  // Determines if confirm/decline buttons should show
  // Only shows for providers on conversations that are still pending
  const showConfirmDecline = userRole === 'provider' &&
    activeConversation &&
    !activeConversation.status &&
    activeConversation.bookingId;

  if (loading) {
    return (
      <div className="messages-loading">
        <div className="loading-spinner"></div>
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="messages-container">

      {/* ========== LEFT PANEL — Conversation list ========== */}
      <div className="conversations-list">
        <h3 className="conversations-title">Conversations</h3>

        {conversations.length > 0 ? (
          conversations.map(conv => (
            <div
              key={conv.id}
              className={`conversation-item ${activeConversation?.id === conv.id ? 'conversation-active' : ''}`}
              onClick={() => setActiveConversation(conv)}
            >
              {/* Avatar with first letter */}
              <div className="conversation-avatar">
                {userRole === 'customer'
                  ? conv.providerName?.charAt(0)
                  : conv.customerName?.charAt(0)}
              </div>
              <div className="conversation-info">
                <h4>
                  {userRole === 'customer' ? conv.providerName : conv.customerName}
                </h4>
                <p className="conversation-preview">
                  {conv.lastMessage || 'Start a conversation'}
                </p>
                <p className="conversation-booking">
                  Re: {conv.lovedOneName}
                </p>
                {/* Status badge on conversation */}
                {conv.status === 'confirmed' && (
                  <span className="conv-status-badge conv-status-confirmed">✅ Confirmed</span>
                )}
                {conv.status === 'declined' && (
                  <span className="conv-status-badge conv-status-declined">❌ Declined</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="conversations-empty">
            <p>No conversations yet</p>
            <p className="conversations-empty-sub">
              {userRole === 'customer'
                ? 'Book a visit to start messaging your caretaker'
                : 'Claim a job to start messaging customers'}
            </p>
          </div>
        )}
      </div>

      {/* ========== RIGHT PANEL — Chat ========== */}
      <div className="chat-panel">

        {activeConversation ? (
          <>
            {/* Chat header */}
            <div className="chat-header">
              <div className="chat-header-avatar">
                {userRole === 'customer'
                  ? activeConversation.providerName?.charAt(0)
                  : activeConversation.customerName?.charAt(0)}
              </div>
              <div className="chat-header-info">
                <h4>
                  {userRole === 'customer'
                    ? activeConversation.providerName
                    : activeConversation.customerName}
                </h4>
                <p>Re: {activeConversation.lovedOneName}</p>
              </div>

              {/* Conversation status shown in header */}
              {activeConversation.status === 'confirmed' && (
                <span className="conv-status-badge conv-status-confirmed" style={{ marginLeft: 'auto' }}>
                  ✅ Confirmed
                </span>
              )}
              {activeConversation.status === 'declined' && (
                <span className="conv-status-badge conv-status-declined" style={{ marginLeft: 'auto' }}>
                  ❌ Declined
                </span>
              )}
            </div>

            {/* ========== CONFIRM/DECLINE BUTTONS ==========
                Only shown to providers on pending conversations
                Sits above the messages so it's always visible */}
            {showConfirmDecline && (
              <div className="confirm-decline-bar">
                <div className="confirm-decline-text">
                  <p>This customer is requesting a visit. Would you like to take this job?</p>
                </div>
                <div className="confirm-decline-buttons">
                  <button
                    className="btn-confirm-job"
                    onClick={handleConfirmJob}
                    disabled={confirmingJob}
                  >
                    {confirmingJob ? 'Confirming...' : '✅ Confirm Job'}
                  </button>
                  <button
                    className="btn-decline-job"
                    onClick={handleDeclineJob}
                    disabled={confirmingJob}
                  >
                    ❌ Decline
                  </button>
                </div>
              </div>
            )}

            {/* Messages area */}
            <div className="chat-messages">
              {messages.length > 0 ? (
                messages.map(message => (
                  <div
                    key={message.id}
                    className={`message ${
                      // System messages centered, user messages left/right
                      message.type === 'confirmation' || message.type === 'decline' || message.type === 'system'
                        ? 'message-system'
                        : message.senderId === user?.uid
                          ? 'message-sent'
                          : 'message-received'
                    }`}
                  >
                    {/* System messages styled differently */}
                    {(message.type === 'confirmation' || message.type === 'decline' || message.type === 'system') ? (
                      <div className={`system-message ${message.type === 'confirmation' ? 'system-confirmed' : message.type === 'decline' ? 'system-declined' : 'system-info'}`}>
                        <p>{message.text}</p>
                      </div>
                    ) : (
                      <>
                        <div className="message-bubble">
                          <p>{message.text}</p>
                        </div>
                        <span className="message-time">
                          {getRelativeTime(message.createdAt)}
                        </span>
                      </>
                    )}
                  </div>
                ))
              ) : (
                <div className="chat-empty">
                  <p>No messages yet — say hello!</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message input — disabled if conversation is concluded */}
            {activeConversation.status !== 'declined' ? (
              <div className="chat-input-area">
                <textarea
                  className="chat-input"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  rows={1}
                  maxLength={1000}
                />
                <button
                  className="chat-send-btn"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                >
                  Send
                </button>
              </div>
            ) : (
              // Declined conversation — show closed message
              <div className="chat-closed">
                <p>This conversation has been closed.</p>
              </div>
            )}
          </>
        ) : (
          <div className="chat-empty-state">
            <div className="chat-empty-icon">💬</div>
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the left to start messaging</p>
          </div>
        )}

      </div>
    </div>
  );
}

export default Messages;